import React, { useState, useEffect, useCallback } from 'react';
import PasienLayout from './PasienLayout'; 
import axiosInstance from '../../api/axios';
import { useAuth } from '../../context/AuthContext'; // 🟢 Import AuthContext
import '../admin/Dashboard.css';
import './pasien-pages.css';

const STATUS_LABELS = {
  pending:    { label: 'Menunggu Konfirmasi', color: '#b45309', bg: '#fef3c7' },
  confirmed:  { label: 'Dikonfirmasi',        color: '#1d4ed8', bg: '#dbeafe' },
  checked_in: { label: 'Sedang Dilayani',     color: '#6d28d9', bg: '#ede9fe' },
  completed:  { label: 'Selesai',             color: '#065f46', bg: '#d1fae5' },
  cancelled:  { label: 'Dibatalkan',          color: '#991b1b', bg: '#fee2e2' },
};

function StatusBadge({ status }) {
  const s = STATUS_LABELS[status] ?? { label: status, color: '#374151', bg: '#f3f4f6' };
  return (
    <span className="pasien-status" style={{ color: s.color, background: s.bg, padding: '4px 8px', borderRadius: '6px', fontSize: '12px', fontWeight: 600, display: 'inline-block' }}>
      {s.label}
    </span>
  );
}

const fmtTanggalDaftar = (val) => {
  if (!val) return '—';
  const opts = { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' };
  
  const s = String(val).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s.slice(0, 10))) {
    const [y, m, d] = s.slice(0, 10).split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('id-ID', opts);
  }
  
  const parsed = new Date(val);
  if (Number.isNaN(parsed.getTime())) return '—';
  
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate()).toLocaleDateString('id-ID', opts);
};

export default function Antrian() {
  const { user } = useAuth(); // 🟢 Ambil data user login dari Context
  const [antrian, setAntrian] = useState(null); 
  const [namaProfile, setNamaProfile] = useState(''); // 🟢 State penampung dari profil
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchData = useCallback(async () => {
    setError(null);
    try {
      // 🟢 1. Request profile pasien (sama persis dengan Profil.jsx)
      try {
        const resProf = await axiosInstance.get('/pasien/profile');
        const dProf = resProf.data?.data ?? resProf.data;
        setNamaProfile(dProf?.nama ?? '');
      } catch (e) {
        console.error('Error fetching profile in antrian:', e);
      }

      // 🟢 2. Request antrian
      const res = await axiosInstance.get('/pasien/antrian');
      const data = res.data?.data ?? res.data;
      setAntrian(data?.id ? data : null);
    } catch (err) {
      if (err.response?.status === 404) {
        setAntrian(null);
      } else {
        console.error('Error fetching antrian', err);
        setError(err.response?.data?.message ?? 'Gagal memuat antrian. Silakan coba lagi.');
      }
    }
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      await fetchData();
      if (active) setLoading(false);
    })();
    return () => { active = false; };
  }, [fetchData]);

  // 🟢 Ekstraksi nama pasien dengan fallback bertingkat
  const namaPasien = 
    antrian?.pasien?.nama ?? 
    antrian?.nama_pasien ?? 
    namaProfile ?? 
    user?.nama ?? 
    user?.name ?? 
    '—';

  // Ambil data dokter
  const namaDokter = antrian?.dokter?.nama
    ?? antrian?.dokter?.name
    ?? antrian?.dokter_id
    ?? '—';

  const jadwal = antrian?.jadwal_dokter;
  const jamJadwal = antrian?.jam_kunjungan ?? jadwal?.jam_mulai ?? '—';
  const formatJamBersih = jamJadwal.substring(0, 5);

  return (
    <PasienLayout>
      <div className="w-full p-4 md:p-6" style={{ width: '100%', boxSizing: 'border-box' }}>
        
        <header className="pasien-page-header" style={{ marginBottom: '20px' }}>
          <h1 style={{ fontSize: isMobile ? '1.5rem' : '2rem', margin: '0 0 4px 0', fontWeight: 700, color: '#1e293b' }}>
            Antrian
          </h1>
          <p style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>
            Status antrian kunjungan Anda saat ini.
          </p>
        </header>

        {error && (
          <div className="pasien-banner pasien-banner--error" role="alert" style={{ padding: '12px', borderRadius: '8px', marginBottom: '20px', backgroundColor: '#fee2e2', color: '#991b1b', fontSize: '14px' }}>
            {error}
          </div>
        )}

        {loading ? (
          <div className="loading" style={{ textAlign: 'center', padding: '40px 0', color: '#64748b' }}>
            Memuat antrian…
          </div>
        ) : antrian ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: isMobile ? '100%' : 520 }}>

            {/* Kartu Nomor Antrian */}
            <div style={{
              background: '#eff6ff',
              border: '1px solid #bfdbfe',
              borderRadius: 12,
              padding: isMobile ? '20px 16px' : '24px 28px',
              textAlign: 'center',
            }}>
              <div style={{ fontSize: 13, color: '#3b82f6', fontWeight: 600, marginBottom: 4 }}>
                Nomor Antrian
              </div>
              <div style={{ fontSize: isMobile ? '2.5rem' : '32px', fontWeight: 800, color: '#1d4ed8', letterSpacing: 1, wordBreak: 'break-word', margin: '6px 0' }}>
                {antrian.no_antrian ?? '—'}
              </div>
              <div style={{ marginTop: 10 }}>
                <StatusBadge status={antrian.status} />
              </div>
              {antrian.estimasi_waktu_label && (
                <div style={{ marginTop: 14, fontSize: '14px', color: '#1e40af', fontWeight: 600 }}>
                  Estimasi jam kehadiran: {antrian.estimasi_waktu_label}
                </div>
              )}
            </div>

            {/* Detail Kunjungan */}
            <div style={{
              background: '#fff',
              border: '1px solid #e5e7eb',
              borderRadius: 12,
              padding: '20px 24px',
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
              fontSize: '14px',
            }}>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4, color: '#1f2937' }}>
                Detail Kunjungan
              </div>
              {[
                ['Nama Pasien', namaPasien],
                ['Dokter',      namaDokter],
                ['Tanggal',     fmtTanggalDaftar(antrian.tanggal_pendaftaran)],
                ['Jam',         `${formatJamBersih} WIB`],
                ['Keluhan',     antrian.keluhan ?? '—'],
              ].map(([label, val]) => (
                <div key={label} style={{ display: 'flex', flexDirection: 'row', gap: 8, alignItems: 'flex-start' }}>
                  <span style={{ minWidth: 100, color: '#6b7280', fontWeight: 600 }}>{label}</span>
                  <span style={{ color: '#111827', flex: 1 }}>{val}</span>
                </div>
              ))}
            </div>

            {/* Petunjuk Banner Sesuai Status */}
            {antrian.status === 'pending' && (
              <div className="pasien-banner pasien-banner--info" role="status" style={{ margin: 0, padding: '12px', borderRadius: '8px', fontSize: '13px', backgroundColor: '#fef3c7', color: '#b45309' }}>
                Pendaftaran Anda sedang menunggu konfirmasi dari petugas.
              </div>
            )}
            {antrian.status === 'confirmed' && (
              <div className="pasien-banner pasien-banner--success" role="status" style={{ margin: 0, padding: '12px', borderRadius: '8px', fontSize: '13px', backgroundColor: '#d1fae5', color: '#065f46' }}>
                Pendaftaran dikonfirmasi. Harap datang sesuai jadwal dan lakukan check-in di loket.
              </div>
            )}
            {antrian.status === 'checked_in' && (
              <div className="pasien-banner pasien-banner--success" role="status" style={{ margin: 0, padding: '12px', borderRadius: '8px', fontSize: '13px', backgroundColor: '#ede9fe', color: '#6d28d9' }}>
                Anda sedang dalam proses pelayanan. Silakan menunggu dipanggil oleh dokter.
              </div>
            )}
          </div>
        ) : (
          <div className="pasien-empty" style={{ textAlign: 'center', padding: '40px 20px', color: '#64748b', border: '1px dashed #cbd5e1', borderRadius: '12px', backgroundColor: '#f8fafc' }}>
            Anda tidak memiliki antrian aktif saat ini.
          </div>
        )}
      </div>
    </PasienLayout>
  );
}